import {
  FileSpreadsheet,
  MonitorSmartphone,
  Puzzle,
  Boxes,
  PenTool,
  LayoutDashboard,
  GraduationCap,
  BookOpen,
  Film,
  Store,
  Package,
  Receipt,
  Percent,
  BarChart3,
  Settings,
  Video,
  FileText,
  Link2,
  Download,
  ClipboardCheck,
} from "lucide-react";

// Identidad visual del pilar Marketplace (naranja dorado).
export const MKT_GOLD = "#D97706";
export const MKT_TINT = "rgba(217, 119, 6, 0.10)";

// 9 categorías de productos digitales.
export const CATEGORIAS_DIGITALES = [
  { id: "plantillas", label: "Plantillas profesionales", Icon: FileSpreadsheet, desc: "Excel, PDF y formatos listos para presupuestos, metrados y valorizaciones." },
  { id: "software", label: "Software y aplicaciones", Icon: MonitorSmartphone, desc: "Programas y apps para gestión, cálculo y control de obras." },
  { id: "plugins", label: "Plugins y extensiones", Icon: Puzzle, desc: "Complementos para AutoCAD, Revit, Excel y más herramientas." },
  { id: "bim", label: "Recursos BIM", Icon: Boxes, desc: "Familias, modelos y objetos paramétricos para proyectos BIM." },
  { id: "cad", label: "Recursos CAD", Icon: PenTool, desc: "Bloques, detalles constructivos y planos base en formato CAD." },
  { id: "dashboards", label: "Dashboards e indicadores", Icon: LayoutDashboard, desc: "Tableros de control y KPIs para el seguimiento de proyectos." },
  { id: "cursos", label: "Cursos independientes", Icon: GraduationCap, desc: "Formación creada por especialistas fuera del catálogo oficial." },
  { id: "libros", label: "Libros digitales", Icon: BookOpen, desc: "eBooks, guías técnicas y manuales de la industria." },
  { id: "multimedia", label: "Recursos multimedia", Icon: Film, desc: "Videos, renders, texturas y activos gráficos para construcción." },
];

// Categorías permitidas para PRODUCTOS (excluye "Cursos independientes").
export const PRODUCT_CATEGORIES = CATEGORIAS_DIGITALES.filter((c) => c.id !== "cursos");

// Subcategorías sugeridas por categoría (solo UI).
export const SUBCATEGORIAS = {
  plantillas: ["Presupuestos y APU", "Metrados", "Valorizaciones", "Cronogramas", "Otros"],
  software: ["Gestión de obra", "Cálculo estructural", "Control de costos", "Móvil / APK", "Otros"],
  plugins: ["AutoCAD", "Revit", "Excel", "SketchUp", "Otros"],
  bim: ["Familias Revit", "Objetos paramétricos", "Plantillas BIM", "Otros"],
  cad: ["Bloques", "Detalles constructivos", "Planos base", "Otros"],
  dashboards: ["Power BI", "Excel", "Looker Studio", "Otros"],
  libros: ["Manuales técnicos", "Guías", "Normativa", "Otros"],
  multimedia: ["Renders", "Texturas", "Videos", "Otros"],
};

// Estados visuales de un PRODUCTO.
export const ESTADOS_PRODUCTO = {
  borrador: { label: "Borrador", color: "#475569", bg: "#F1F5F9" },
  en_revision: { label: "En revisión", color: "#B45309", bg: "#FEF3C7" },
  aprobado: { label: "Aprobado", color: "#1D4ED8", bg: "#DBEAFE" },
  publicado: { label: "Publicado", color: "#15803D", bg: "#DCFCE7" },
  rechazado: { label: "Rechazado", color: "#B91C1C", bg: "#FEE2E2" },
  pausado: { label: "Pausado", color: "#334155", bg: "#E2E8F0" },
};

// Estados visuales de una TIENDA.
export const ESTADOS_TIENDA = {
  borrador: { label: "Borrador", color: "#475569", bg: "#F1F5F9" },
  en_revision: { label: "En revisión", color: "#B45309", bg: "#FEF3C7" },
  activa: { label: "Activa", color: "#15803D", bg: "#DCFCE7" },
};

// Países disponibles (solo UI, multi-país Latam).
export const PAISES_MKT = ["Perú", "México", "Colombia", "Chile", "Ecuador", "Bolivia", "Argentina"];

// Licencias de uso (solo UI).
export const LICENCIAS_MKT = [
  "Uso personal",
  "Uso comercial",
  "Uso educativo",
  "Licencia extendida",
];

/* ----------------------------------------------------------------------------
 * Persistencia SIMULADA en localStorage (demostración visual, sin backend/DB).
 * -------------------------------------------------------------------------- */
const TIENDA_KEY = "cl_mkt_tienda";
const PRODUCTOS_KEY = "cl_mkt_productos";

export function getTienda() {
  try {
    return JSON.parse(localStorage.getItem(TIENDA_KEY)) || null;
  } catch {
    return null;
  }
}
export function saveTienda(tienda) {
  localStorage.setItem(TIENDA_KEY, JSON.stringify(tienda));
}
export function clearTienda() {
  localStorage.removeItem(TIENDA_KEY);
}
export function getProductos() {
  try {
    return JSON.parse(localStorage.getItem(PRODUCTOS_KEY)) || [];
  } catch {
    return [];
  }
}
export function saveProductos(lista) {
  localStorage.setItem(PRODUCTOS_KEY, JSON.stringify(lista));
}

// Productos de ejemplo (preliminar) para ilustrar estados y acciones.
export const PRODUCTOS_EJEMPLO = [
  { id: "ej-1", titulo: "Plantilla APU en Excel", categoria: "plantillas", precio: "25.00", estado: "publicado" },
  { id: "ej-2", titulo: "Familia BIM · Muro cortina", categoria: "bim", precio: "40.00", estado: "en_revision" },
  { id: "ej-3", titulo: "Dashboard de control de obra", categoria: "dashboards", precio: "60.00", estado: "borrador" },
];

// ----------------------------------------------------------------------------
// CURSOS (instructores independientes) — solo UI, persistencia simulada.
// ----------------------------------------------------------------------------
export const COURSE_CATEGORIES = [
  { id: "gestion", label: "Gestión de obra" },
  { id: "estructuras", label: "Estructuras" },
  { id: "bim", label: "BIM y modelado" },
  { id: "cad", label: "CAD y planos" },
  { id: "costos", label: "Costos y presupuestos" },
  { id: "seguridad", label: "Seguridad y SST" },
  { id: "software", label: "Software especializado" },
  { id: "normativa", label: "Normativa" },
];

export const NIVELES_CURSO = ["Básico", "Intermedio", "Avanzado"];
export const IDIOMAS_CURSO = ["Español", "Inglés", "Portugués"];

export const TIPOS_LECCION = [
  { id: "video", label: "Video", Icon: Video },
  { id: "documento", label: "Documento", Icon: FileText },
  { id: "enlace", label: "Enlace", Icon: Link2 },
  { id: "archivo", label: "Archivo descargable", Icon: Download },
  { id: "evaluacion", label: "Evaluación", Icon: ClipboardCheck },
];

const CURSOS_KEY = "cl_mkt_cursos";
export function getCursos() {
  try {
    return JSON.parse(localStorage.getItem(CURSOS_KEY)) || [];
  } catch {
    return [];
  }
}
export function saveCursos(lista) {
  localStorage.setItem(CURSOS_KEY, JSON.stringify(lista));
}

export const CURSOS_EJEMPLO = [
  {
    id: "ec-1",
    titulo: "Metrados y presupuestos desde cero",
    instructor: "Estudio Digital Constructor",
    categoria: "costos",
    modulos: 4,
    lecciones: 18,
    precio: "90.00",
    estado: "publicado",
    updatedAt: "2026-06-10",
    comentarios: [],
  },
  {
    id: "ec-2",
    titulo: "Introducción a Revit para edificaciones",
    instructor: "BIM Perú",
    categoria: "bim",
    modulos: 6,
    lecciones: 25,
    precio: "0.00",
    estado: "en_revision",
    updatedAt: "2026-06-11",
    comentarios: [{ autor: "Equipo de revisión", texto: "Tu curso está en cola de revisión. Te avisaremos pronto." }],
  },
  {
    id: "ec-3",
    titulo: "Seguridad en obra (SST)",
    instructor: "Estudio Digital Constructor",
    categoria: "seguridad",
    modulos: 3,
    lecciones: 12,
    precio: "45.00",
    estado: "rechazado",
    updatedAt: "2026-06-09",
    comentarios: [
      { autor: "Equipo de revisión", texto: "La portada no cumple la resolución mínima. Actualízala y reenvía." },
      { autor: "Equipo de revisión", texto: "Falta la descripción completa del módulo 2." },
    ],
  },
];

// Menú lateral exclusivo del Centro del Vendedor.
export const SELLER_MENU = [
  { to: "/marketplace", end: true, label: "Mi Tienda", Icon: Store, key: "mi-tienda" },
  { to: "/marketplace/productos", label: "Mis Productos", Icon: Package, key: "productos" },
  { to: "/marketplace/cursos", label: "Mis Cursos", Icon: GraduationCap, key: "cursos" },
  { to: "/marketplace/ventas", label: "Mis Ventas", Icon: Receipt, key: "ventas" },
  { to: "/marketplace/comisiones", label: "Comisiones", Icon: Percent, key: "comisiones" },
  { to: "/marketplace/estadisticas", label: "Estadísticas", Icon: BarChart3, key: "estadisticas" },
  { to: "/marketplace/configuracion", label: "Configuración", Icon: Settings, key: "configuracion" },
];
